// Ordering and country display for the Real Time area list. Pure: no React, router or Firebase.
import { areaCountry, realtimeDefaultCountry } from '../config/countries';
import { REALTIME_GEO_TYPES } from '../config/products';
import { partitionBy } from './partitionAreas';

interface CountryArea {
  type: string;
  country_code?: string | null;
}

/**
 * For display: a Real Time polygon with no usable country is given the country Real Time serves.
 * Other areas, and areas that already have a country, come back unchanged. Returns a copy; the
 * stored area is never touched.
 */
export function withRealtimeCountry<T extends CountryArea>(area: T): T {
  if (!REALTIME_GEO_TYPES.includes(area.type) || areaCountry(area) != null) return area;
  return { ...area, country_code: realtimeDefaultCountry() };
}

/**
 * Splits areas into the monitored ones (turned on) and the rest. Inside each group, areas with
 * Real Time data come first, then by name.
 */
export function partitionMonitored<T extends { id: string; name: string }>(
  areas: T[],
  isMonitored: (id: string) => boolean,
  dataCount: (area: T) => number,
): { monitored: T[]; other: T[] } {
  const { matching, rest } = partitionBy(
    areas,
    (a) => isMonitored(a.id),
    (a) => (dataCount(a) > 0 ? 0 : 1),
  );
  return { monitored: matching, other: rest };
}

/**
 * Leaves out the areas whose Real Time data can't be shown in this environment (`isElsewhere`),
 * and counts them. `hiddenMonitored` is how many of the hidden areas are turned on, so the page
 * can still tell the user about an active monitor. Returns a new array; the input is untouched.
 */
export function hideElsewhereAreas<T extends { id: string }>(
  areas: T[],
  isElsewhere: (area: T) => boolean,
  isMonitored: (id: string) => boolean,
): { visible: T[]; hidden: number; hiddenMonitored: number } {
  const hiddenAreas = areas.filter(isElsewhere);
  return {
    visible: areas.filter((a) => !isElsewhere(a)),
    hidden: hiddenAreas.length,
    hiddenMonitored: hiddenAreas.filter((a) => isMonitored(a.id)).length,
  };
}

/** The muted line shown under the lists; null when nothing is hidden. */
export function hiddenAreasNote(
  hidden: number,
  hiddenMonitored: number,
  envLabels: string[],
): string | null {
  if (hidden <= 0) return null;
  const labels = Array.from(new Set(envLabels));
  const source = labels.length === 1 ? labels[0] : 'another environment\u2019s';
  const noun = hidden === 1 ? 'area uses' : 'areas use';
  const verb = hidden === 1 ? 'is' : 'are';
  const monitored =
    hiddenMonitored > 0 ? ` (${hiddenMonitored} ${hiddenMonitored === 1 ? 'is' : 'are'} being monitored)` : '';
  return `${hidden} ${noun} ${source} data and ${verb} hidden in this environment${monitored}.`;
}
